import { useEffect, useRef, useState, type KeyboardEvent as ReactKeyboardEvent } from 'react';
import { matchPath, NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom';
import {
  LayoutDashboard,
  Users,
  UsersRound,
  FileText,
  BarChart3,
  MessageCircle,
  Settings as SettingsIcon,
  LogOut,
  Wrench,
  Images,
  Stamp,
  FileSpreadsheet,
  Moon,
  ShieldCheck,
  BookOpen,
  CalendarClock,
  ClipboardCheck,
  FilePenLine,
  ChevronDown,
  PanelLeftClose,
  PanelLeftOpen,
  Menu,
  X,
  type LucideIcon,
} from 'lucide-react';
import { useAuthStore } from '../store/auth';
import { useChatUnread } from '../store/chat';
import { useSocket } from '../hooks/useSocket';
import { useBoardUpdates } from '../hooks/useBoardUpdates';
import { useTechniciansStore } from '../store/technicians';
import { requestNotificationPermission, showBrowserNotification } from '../utils/browserNotify';
import { playNotificationSound } from '../utils/sound';
import type { ChatMessage, GeneralChatMessage } from '../types';

const CHANNEL_LABELS: Record<string, string> = { general: 'общий чат', 'grease-trap': 'чат «Жирики»' };
import NotificationsBell from './NotificationsBell';
import UpdateBanner from './UpdateBanner';
import SearchInput from './SearchInput';

// Название программы — Control Brigade, под ним компания (владелец, 08.10.2026: «название поменяй
// на Control Brigade, а ниже уже Труба-Дело»). В документах и SMS — по-прежнему «Труба-Дело»
const BASE_TITLE = 'Control Brigade';
const ICON_SIZE = 17;

interface NavItem {
  path: string;
  label: string;
  icon: LucideIcon;
  /** Подсвечивать только на точном адресе — иначе «/» горел бы на любой странице */
  end?: boolean;
  /** Счётчик справа от пункта */
  badge?: 'chatUnread';
}

interface NavSection {
  /** Под этим ключом запоминается, свёрнута ли группа */
  id: string;
  /** Разделы без заголовка не сворачиваются */
  title?: string;
  /** Прижать к низу меню */
  bottom?: boolean;
  items: NavItem[];
}

// Вкладок становится всё больше, и плоский список из полутора десятков пунктов читался плохо —
// теперь они разложены по группам. Новый раздел — одна строка здесь, а не ещё один NavLink
const NAV_SECTIONS: NavSection[] = [
  {
    id: 'main',
    items: [
      { path: '/', label: 'Диспетчерская', icon: LayoutDashboard, end: true },
      { path: '/chat', label: 'Чат', icon: MessageCircle, badge: 'chatUnread' },
    ],
  },
  {
    id: 'orders',
    title: 'Заявки',
    items: [
      { path: '/night-orders', label: 'Ночные заявки', icon: Moon },
      { path: '/maintenance', label: 'Обслуживание', icon: CalendarClock },
      { path: '/inspections', label: 'ПрофОсмотры', icon: ClipboardCheck },
    ],
  },
  {
    id: 'documents',
    title: 'Документы',
    items: [
      { path: '/acts', label: 'Акты', icon: FileText },
      { path: '/document-editor', label: 'Правка документов', icon: FilePenLine },
      { path: '/documents', label: 'Бланки и шаблоны', icon: Stamp },
      { path: '/onec-export', label: 'Выгрузка в 1С', icon: FileSpreadsheet },
      { path: '/media', label: 'Медиафайлы', icon: Images },
    ],
  },
  {
    id: 'team',
    title: 'Команда',
    items: [
      { path: '/employees', label: 'Сотрудники', icon: Users },
      { path: '/crews', label: 'Составы', icon: UsersRound },
      { path: '/technician-reports', label: 'Отчёты', icon: BarChart3 },
    ],
  },
  {
    id: 'reference',
    title: 'Справка',
    items: [
      { path: '/kb', label: 'Справочник', icon: BookOpen },
      { path: '/counterparties', label: 'Контрагенты', icon: ShieldCheck },
    ],
  },
  {
    id: 'settings',
    bottom: true,
    items: [{ path: '/settings', label: 'Настройки', icon: SettingsIcon }],
  },
];

const COLLAPSED_GROUPS_KEY = 'truba-delo-nav-collapsed';
const COMPACT_KEY = 'truba-delo-nav-compact';

// Хранилище браузера бывает недоступно (приватный режим, запрет в настройках) —
// тогда меню просто не помнит свой вид между заходами, но работает
function readStorage(key: string): string | null {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
}

function writeStorage(key: string, value: string) {
  try {
    localStorage.setItem(key, value);
  } catch {
    // не запомнилось — до перезагрузки меню всё равно выглядит так, как выбрали
  }
}

function loadCollapsedGroups(): Set<string> {
  try {
    const parsed: unknown = JSON.parse(readStorage(COLLAPSED_GROUPS_KEY) ?? '[]');
    return new Set(Array.isArray(parsed) ? parsed.filter((id): id is string => typeof id === 'string') : []);
  } catch {
    return new Set();
  }
}

// «ё» и «е» пишут вперемешку: «Отчёты» должны находиться и по «отчеты»
function normalizeForSearch(text: string): string {
  return text.trim().toLowerCase().replace(/ё/g, 'е');
}

// Кроме названия пункта смотрим и на название группы: «документы» покажет всю группу —
// в том числе «Бланки и шаблоны», которые раньше в меню так и назывались «Документы»
function filterSections(query: string): NavSection[] {
  return NAV_SECTIONS.map((section) =>
    section.title && normalizeForSearch(section.title).includes(query)
      ? section
      : { ...section, items: section.items.filter((item) => normalizeForSearch(item.label).includes(query)) },
  ).filter((section) => section.items.length > 0);
}

function isItemActive(item: NavItem, pathname: string): boolean {
  return matchPath({ path: item.path, end: item.end ?? false }, pathname) !== null;
}

export default function Layout() {
  const user = useAuthStore((s) => s.user);
  const logout = useAuthStore((s) => s.logout);
  const unreadCount = useChatUnread((s) => s.unreadCount);
  const refreshUnread = useChatUnread((s) => s.refresh);
  const socket = useSocket('chat');
  const location = useLocation();
  // Общий список сантехников (выпадающие списки назначения, фильтры) обновляется по
  // сигналу с сервера — иначе нового сотрудника не было видно до перезагрузки страницы
  const reloadTechnicians = useTechniciansStore((s) => s.reload);
  useBoardUpdates(['employees'], reloadTechnicians);

  useEffect(() => {
    requestNotificationPermission();
    refreshUnread();
  }, [refreshUnread]);

  useEffect(() => {
    if (!socket) return;
    const onMessage = (message: ChatMessage) => {
      if (message.senderId === user?.id) return;
      // На странице чата непрочитанное сразу отмечается прочитанным сервером —
      // не дублируем счётчик, просто пересчитываем после короткой паузы
      refreshUnread();
      if (location.pathname !== '/chat') {
        playNotificationSound();
      }
      // Метка чата: прочитают тред — уведомление Windows закроется само (NotificationsBell).
      // Пишет диспетчерам сантехник, поэтому тред — по отправителю
      showBrowserNotification(`${message.sender?.fullName ?? 'Новое сообщение'} — ${BASE_TITLE}`, message.text, `direct:${message.senderId}`);
    };
    socket.on('chat:message', onMessage);

    const onChannelMessage = (message: GeneralChatMessage) => {
      if (message.senderId === user?.id) return;
      refreshUnread();
      if (location.pathname !== '/chat') {
        playNotificationSound();
      }
      showBrowserNotification(`${message.sender.fullName} — ${CHANNEL_LABELS[message.channel]}`, message.text, `channel:${message.channel}`);
    };
    socket.on('chat:channel:message', onChannelMessage);

    return () => {
      socket.off('chat:message', onMessage);
      socket.off('chat:channel:message', onChannelMessage);
    };
  }, [socket, user?.id, location.pathname, refreshUnread]);

  useEffect(() => {
    document.title = unreadCount > 0 ? `(${unreadCount}) ${BASE_TITLE}` : BASE_TITLE;
  }, [unreadCount]);

  useEffect(() => {
    if (location.pathname === '/chat') {
      refreshUnread();
    }
  }, [location.pathname, refreshUnread]);

  return (
    <div className="app-shell">
      <Sidebar chatUnread={unreadCount} userName={user?.fullName} onLogout={logout} />
      <main className="app-content">
        <div className="topbar">
          <div className="topbar-actions">
            <NotificationsBell />
          </div>
        </div>
        <UpdateBanner />
        <div className="app-content-body">
          <Outlet />
        </div>
      </main>
    </div>
  );
}

interface SidebarProps {
  chatUnread: number;
  userName?: string;
  onLogout: () => void;
}

function Sidebar({ chatUnread, userName, onLogout }: SidebarProps) {
  const location = useLocation();
  const navigate = useNavigate();
  const [compact, setCompact] = useState(() => readStorage(COMPACT_KEY) === '1');
  const [collapsedGroups, setCollapsedGroups] = useState(loadCollapsedGroups);
  const [query, setQuery] = useState('');
  // На телефоне меню открыто только на той странице, где его открыли: любой переход
  // (пункт меню, Enter в поиске, «Назад» в браузере) закрывает его сам
  const [menuOpenedOn, setMenuOpenedOn] = useState<string | null>(null);
  const mobileMenuOpen = menuOpenedOn === location.pathname;
  const navRef = useRef<HTMLElement>(null);

  // На невысоком экране меню прокручивается — пункт открытой страницы держим на виду,
  // даже если он в самом низу списка
  useEffect(() => {
    navRef.current?.querySelector('a.active')?.scrollIntoView({ block: 'nearest' });
  }, [location.pathname, mobileMenuOpen]);

  useEffect(() => {
    if (!mobileMenuOpen) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setMenuOpenedOn(null);
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [mobileMenuOpen]);

  const search = normalizeForSearch(query);
  const sections = search ? filterSections(search) : NAV_SECTIONS;
  const firstMatch = search ? sections[0]?.items[0] : undefined;
  // Группу открытой сейчас страницы свернуть нельзя — иначе не видно, где находишься
  const activeGroupId = NAV_SECTIONS.find(
    (section) => section.title && section.items.some((item) => isItemActive(item, location.pathname)),
  )?.id;

  function toggleGroup(id: string) {
    const next = new Set(collapsedGroups);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    setCollapsedGroups(next);
    writeStorage(COLLAPSED_GROUPS_KEY, JSON.stringify([...next]));
  }

  function toggleCompact() {
    const next = !compact;
    setCompact(next);
    // В узком меню поля поиска нет — недопечатанный запрос оставил бы от меню пару значков
    setQuery('');
    writeStorage(COMPACT_KEY, next ? '1' : '0');
    // Карта Leaflet подгоняет себя под размер только по resize окна, а здесь меняется лишь
    // ширина области страницы — без этого сбоку на карте оставалась бы серая полоса
    requestAnimationFrame(() => window.dispatchEvent(new Event('resize')));
  }

  function afterNavigate() {
    setQuery('');
    setMenuOpenedOn(null);
  }

  function handleSearchKeyDown(e: ReactKeyboardEvent<HTMLDivElement>) {
    const input = e.target as HTMLElement;
    if (e.key === 'Enter' && firstMatch) {
      navigate(firstMatch.path);
      afterNavigate();
      input.blur();
    } else if (e.key === 'Escape') {
      if (query) {
        // Первый Esc только очищает поиск, меню на телефоне закроет уже следующий
        e.stopPropagation();
        setQuery('');
      } else {
        input.blur();
      }
    }
  }

  const sidebarClass = ['sidebar', compact && 'sidebar-compact', mobileMenuOpen && 'sidebar-mobile-open']
    .filter(Boolean)
    .join(' ');

  return (
    <>
      <aside className={sidebarClass}>
        <div className="sidebar-title">
          <span className="sidebar-brand">
            <Wrench size={19} strokeWidth={2.3} />
            <span className="sidebar-brand-text">
              <span className="sidebar-brand-name">Control Brigade</span>
              <span className="sidebar-brand-company">Труба-Дело</span>
            </span>
          </span>
          <button
            type="button"
            className="sidebar-icon-btn sidebar-compact-toggle"
            onClick={toggleCompact}
            title={compact ? 'Развернуть меню' : 'Свернуть меню до значков'}
            aria-label={compact ? 'Развернуть меню' : 'Свернуть меню'}
          >
            {compact ? <PanelLeftOpen size={ICON_SIZE} /> : <PanelLeftClose size={ICON_SIZE} />}
          </button>
          <button
            type="button"
            className="sidebar-menu-toggle"
            onClick={() => setMenuOpenedOn(mobileMenuOpen ? null : location.pathname)}
            aria-expanded={mobileMenuOpen}
          >
            {mobileMenuOpen ? <X size={ICON_SIZE} /> : <Menu size={ICON_SIZE} />}
            Меню
          </button>
        </div>
        <div className="sidebar-body">
          <div className="sidebar-search" onKeyDown={handleSearchKeyDown}>
            <SearchInput value={query} onChange={setQuery} placeholder="Найти раздел…" compact />
          </div>
          <nav ref={navRef}>
            {sections.map((section) => {
              // Во время поиска найденное показываем целиком, без сворачивания
              const canCollapse = Boolean(section.title) && !search && section.id !== activeGroupId;
              const collapsed = canCollapse && collapsedGroups.has(section.id);
              const sectionClass = [
                'nav-section',
                collapsed && 'nav-group-collapsed',
                section.bottom && 'nav-section-bottom',
              ]
                .filter(Boolean)
                .join(' ');
              return (
                <div key={section.id} className={sectionClass}>
                  {section.title &&
                    (canCollapse ? (
                      <button
                        type="button"
                        className="nav-group-header"
                        onClick={() => toggleGroup(section.id)}
                        aria-expanded={!collapsed}
                      >
                        {section.title}
                        <ChevronDown size={14} className="nav-group-chevron" />
                      </button>
                    ) : (
                      <div className="nav-group-header">{section.title}</div>
                    ))}
                  <div className="nav-group-items">
                    {section.items.map((item) => {
                      const count = item.badge === 'chatUnread' ? chatUnread : 0;
                      return (
                        <NavLink
                          key={item.path}
                          to={item.path}
                          end={item.end}
                          title={compact ? item.label : undefined}
                          className={item === firstMatch ? 'nav-first-match' : undefined}
                          onClick={afterNavigate}
                        >
                          <item.icon size={ICON_SIZE} />
                          <span className="nav-label">{item.label}</span>
                          {count > 0 && <span className="badge nav-badge">{count}</span>}
                        </NavLink>
                      );
                    })}
                  </div>
                </div>
              );
            })}
            {sections.length === 0 && <p className="nav-empty muted">Ничего не найдено</p>}
          </nav>
          <div className="sidebar-footer">
            <div className="muted sidebar-user">{userName}</div>
            <button className="btn-link" onClick={onLogout} title={compact ? 'Выйти' : undefined}>
              <LogOut size={15} />
              <span className="sidebar-logout-label">Выйти</span>
            </button>
          </div>
        </div>
      </aside>
      {mobileMenuOpen && <div className="sidebar-backdrop" onClick={() => setMenuOpenedOn(null)} />}
    </>
  );
}
